import React, { useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';
import { FormApi } from 'final-form';
import axios, { CancelTokenSource } from 'axios';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IApiResponse } from 'utils/models';
import Form from 'ui/Form/Form';
import { requiredOnly, validationCallbackOld } from 'utils/validation';
import { PhoneInput, Select, SelectItem } from 'ui/Input';
import Input from 'ui/Input/Input';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Button, { Sizes as ButtonSizes, Themes as ButtonThemes } from 'ui/Button/Button';
import { StatusModal, WarningModal } from 'components/modals';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import api from 'api/api';
import InputHint from 'components/modules/Beneficiaries/InputHint/InputHint';
import { INetwork } from 'components/modules/Beneficiaries/Beneficiaries.model';
import debounce from 'utils/debounce';
import { IBeneficiaryMobile } from './SingleMobileBen.model';

import deleteIcon from 'assets/icons/delete.png';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

const schema = yup.object().shape({
  beneficiaryName: requiredOnly('Full name'),
  networkCode: requiredOnly('Network'),
  beneficiaryWalletNumber: requiredOnly('Wallet Number'),
  walletName: requiredOnly('Wallet name'),
});

interface IFormValues {
  beneficiaryName: string;
  networkCode: string;
  beneficiaryWalletNumber: string;
  walletName: string;
}

interface IEditBenFab {
  data: IBeneficiaryMobile;
  networks: INetwork[];
  onClose: () => void;
}

const EditMobileBen = ({ data, networks, onClose }: IEditBenFab) => {
  const [nameHint, setNameHint] = useState('');
  const [isLookingUp, setLookingUp] = useState(false);
  const clearNameHint = () => setNameHint('');
  const [validatedAccountNo, setValidatedAccountNo] = useState(true);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;

  const [actionType, setActionType] = useState<string>('');
  const [showWarning, setShowWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const source = useRef<CancelTokenSource | null>(null);

  const openWarning = (type: string) => () => {
    setActionType(type);
    setShowWarning(true);
  };
  const closeWarning = () => {
    setShowWarning(false);
  };

  const [showStatus, setShowStatus] = useState(false);
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const validate = useCallback((values: IFormValues) => validationCallbackOld<IFormValues>(schema)(values), []);

  const intialValues: IFormValues = useMemo(() => {
    const iniitalData = {
      beneficiaryName: data.name,
      networkCode: data.networkCode,
      beneficiaryWalletNumber: data.phoneNumber,
      walletName: data.walletName,
    };

    return iniitalData;
  }, []);

  const lookupAccount = (phoneNumber: string, network: string, form: FormType) => {
    if (source.current) {
      source.current.cancel();
    }
    source.current = axios.CancelToken.source();

    api
      .get(
        `transfer/mobilemoneylookup?phoneNumber=${phoneNumber}&network=${network}&transferType=A2W`,
        {},
        true,
        true,
        undefined,
        source.current?.token,
      )
      .then((response) => {
        if (response.accountName === null) {
          setNameHint('Account not found');
          setValidatedAccountNo(false);
          setLookingUp(false);
          form.change('walletName', '');
        } else {
          setNameHint(response.accountName);
          setValidatedAccountNo(true);
          setLookingUp(false);
          form.change('walletName', response.accountName);
        }
      })
      .catch((error: IApiResponse) => {
        setValidatedAccountNo(false);
        if (error.description) {
          console.error(error);
          setNameHint(error.description);
          setLookingUp(false);
          form.change('walletName', '');
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 300), []);

  const handleChangeNetworkType = (form: FormType, values: IFormValues) => ({ target }) => {
    form.mutators.changeFieldValue(target?.name, target?.value); // Override default handleChange()

    if (values.beneficiaryWalletNumber) {
      setLookingUp(true);
      setValidatedAccountNo(false);
      lookupAccount(values.beneficiaryWalletNumber, target.value as string, form);
    }
  };

  const handleChangeWalletNumber = (form: FormType, values: IFormValues) => ({ target }) => {
    const targetValue = target?.value.replace(/[^\d.]/g, '');
    form.change(target?.name, targetValue);
    setLookingUp(true);
    setValidatedAccountNo(false);
    if (!targetValue) {
      setValidatedAccountNo(false);
      setNameHint('');
    }
    if (values.networkCode && targetValue) {
      debounced(targetValue as string, values?.networkCode, form);
    }
  };

  const onDeleteBeneficiary = (id: number) => {
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/mobilemoney/delete', {
        beneficiaryId: id,
      })
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const onSaveBeneficiary = (id: number, values: IFormValues) => {
    const selectedNetwork = networks.find((network) => network.code === values.networkCode);

    const body = {
      ...values,
      beneficiaryId: id,
      network: selectedNetwork?.name,
      networkGipCode: selectedNetwork?.gipCode,
    };

    api
      .put('/beneficiary/mobilemoney/update', body)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
        setSubmitStatus(AxiosStatus.SUCCESS);
      })
      .catch((err: IApiResponse) => {
        setErrorMessage(err.description);
        setSubmitStatus(AxiosStatus.FAILURE);
      })
      .finally(() => {
        setShowStatus(true);
      });
  };

  const openConfirm = (values: IFormValues) => () => {
    setSubmitStatus(AxiosStatus.PENDING);

    return actionType === 'delete' ? onDeleteBeneficiary(data.id) : onSaveBeneficiary(data.id, values);
  };

  return (
    <>
      <Form
        onSubmit={openWarning('save')}
        validate={validate}
        mutators={{ changeFieldValue, setFieldTouched }}
        initialValues={intialValues}
        render={({ handleSubmit, values, form }) => (
          <form onSubmit={handleSubmit}>
            {!downMdScreen && <NavigateBackStep className="mt30" onClick={onClose}>Back</NavigateBackStep>}
            <FieldsLeveler
              label={(
                <>
                  <div>Beneficiary details</div>
                  {!downMdScreen && (
                    <Button size={ButtonSizes.small} onClick={openWarning('delete')}>
                      <img src={deleteIcon} alt="trash-icon" />
                      &nbsp;Delete
                    </Button>
                  )}
                </>
              )}
            >
              <Input name="beneficiaryName" label="Full Name" disabled={submitting} />
              <Select
                name="networkCode"
                label="Network"
                disabled={submitting}
                SelectProps={{
                  onChange: handleChangeNetworkType(form, values),
                }}
              >
                {networks.map(({ name, code }) => (
                  <SelectItem key={code} value={code}>
                    {name}
                  </SelectItem>
                ))}
              </Select>
              <PhoneInput
                name="beneficiaryWalletNumber"
                label="Beneficiary Wallet Number"
                onInput={handleChangeWalletNumber(form, values)}
                disabled={submitting}
              />
              {(nameHint || isLookingUp) && <InputHint hint={nameHint} loading={isLookingUp} onClose={clearNameHint} />}
            </FieldsLeveler>
            <SubmitLeveler>
              {downMdScreen && (
                <Button
                  type="button"
                  theme={ButtonThemes.secondary}
                  fullWidth
                  className="mb20"
                  onClick={openWarning('delete')}
                >
                  Delete Beneficiary
                </Button>
              )}
              <Button
                type="submit"
                fullWidth
                disabled={submitting || !form.getState().dirty || !validatedAccountNo || isLookingUp}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </SubmitLeveler>
            <WarningModal
              open={showWarning}
              submitting={submitting}
              submitName={actionType === 'delete' ? 'Delete' : 'Save'}
              onSubmit={openConfirm(values)}
              onClose={closeWarning}
            >
              {actionType === 'delete'
                ? 'Are you sure you want to delete the Beneficiary?'
                : 'Save changes to Beneficiary?'}
            </WarningModal>
            <StatusModal
              open={showStatus}
              status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
              onRetry={onCloseStatusModal}
              onClose={onCloseStatusModal}
              onRetryText="Continue"
            >
              {submitStatus === AxiosStatus.SUCCESS ? successMessage : errorMessage}
            </StatusModal>
          </form>
        )}
      />
    </>
  );
};

export default EditMobileBen;
